import { reactive } from 'vue'
import type { Ref } from 'vue'

interface EmbedController {
  play: () => void
  pause: () => void
  seek: (seconds: number) => void
  getDuration: () => number
  loadUri: (v: string) => void
  togglePlay: () => void
  addListener: (event: string, callback: (...args: any[]) => void) => void
  iframeElement: HTMLIFrameElement
}
interface SpotifyStore {
  embedController: EmbedController | null
  initialized: boolean
  currentSeconds: number
  duration: number
  isPaused: boolean
  isBuffering: boolean
  currentPodcast: string
  added: boolean
  location: string
  IFrameAPI: Ref<any> | null
  hasPodcastBlock: boolean
  /** First episode on the page. The controller is built with it so the most
   *  likely first click needs no loadUri swap. */
  firstPodcast: string
  /** Set by a play button on hover/focus to skip the idle wait. */
  warmRequested: boolean
  /** Episode the controller currently holds, so a redundant loadUri is skipped. */
  loadedPodcast: string
}

export const spotifyStore: SpotifyStore = reactive({
  embedController: null,
  initialized: false,
  currentSeconds: 0,
  duration: 0,
  isPaused: true,
  isBuffering: false,
  currentPodcast: '',
  added: false,
  location: '',
  IFrameAPI: null,
  hasPodcastBlock: false,
  firstPodcast: '',
  warmRequested: false,
  loadedPodcast: '',
})

// Which <Spotify /> instance the shared store currently belongs to. A
// client-side navigation mounts the next one before unmounting the previous, so
// the outgoing instance has to know the store is no longer its to tear down.
// Module scope on purpose: `<script setup>` is the setup body, so a counter
// declared there would be per-instance and every instance would own the store.
let nextInstance = 0
let storeOwner = 0

export const takeSpotifyInstanceId = () => ++nextInstance
export const claimSpotifyStore = (instance: number) => {
  storeOwner = instance
}
export const ownsSpotifyStore = (instance: number) => storeOwner === instance

// How many podcast blocks are mounted. A count rather than a boolean so two
// blocks on one page don't let the first one's unmount switch the player off
// under the second. Registered on mount, not in setup: setup also runs during
// SSR, where the store is shared between requests, and left latched it kept
// every later page building a controller for a block that isn't there.
let podcastBlocks = 0

export const addPodcastBlock = () => {
  podcastBlocks += 1
  spotifyStore.hasPodcastBlock = true
}

export const removePodcastBlock = () => {
  podcastBlocks = Math.max(0, podcastBlocks - 1)
  spotifyStore.hasPodcastBlock = podcastBlocks > 0
}
